<template>
  <!-- Loading skeleton -->
  <div v-if="loading" class="mb-4">
    <v-row density="comfortable">
      <v-col v-for="i in 4" :key="i" cols="6">
        <v-skeleton-loader type="image, article" class="rounded-lg" />
      </v-col>
    </v-row>
  </div>

  <v-alert v-else-if="error" type="error" variant="tonal" class="mb-4">
    {{ getErrorMessage(error) }}
  </v-alert>

  <v-alert
    v-else-if="!parts || parts.length === 0"
    type="info"
    variant="tonal"
    class="mb-4"
  >
    {{ t(strings.empty) }}
  </v-alert>

  <div v-else class="parts-table-shell">
    <!--
      Desktop / tablet (md+) sidebar that exposes the filter accordion + Clear
      button inline next to the main content. The mobile bottom sheet below
      uses the same `<PartsFilterPanel>` component so behavior and styling
      stay consistent.
    -->
    <aside class="parts-table-shell__sidebar d-none d-md-flex">
      <PartsFilterPanel
        :manufacturers="selectedManufacturers"
        :conditions="selectedConditions"
        :part-types="selectedPartTypes"
        :manufacturer-options="manufacturerOptions"
        :condition-options="conditionOptions"
        :part-type-options="partTypeOptions"
        :condition-display-name="conditionDisplayName"
        :initial-expanded="[0, 1, 2]"
        :show-header="false"
        :show-done="true"
        @update:manufacturers="selectedManufacturers = $event"
        @update:conditions="selectedConditions = $event"
        @update:part-types="selectedPartTypes = $event"
        @done="onDesktopFilterDone"
        @clear="clearFilters"
      />
    </aside>

    <div class="parts-table-shell__main">
    <!-- Search + view toggle + filter (filter button is mobile-only) -->
    <div
      class="d-flex align-center ga-2 flex-wrap"
      :class="hasActiveFilters ? 'mb-2' : 'mb-4'"
    >
      <v-text-field
        v-model="filterText"
        variant="outlined"
        density="comfortable"
        hide-details
        clearable
        :placeholder="t(strings.searchPlaceholder)"
        prepend-inner-icon="mdi-magnify"
        class="parts-search flex-grow-1"
        min-width="0"
      />
      <v-btn
        :icon="
          viewMode === 'grid'
            ? 'mdi-view-list-outline'
            : 'mdi-view-grid-outline'
        "
        variant="outlined"
        color="grey"
        :title="
          viewMode === 'grid'
            ? t(strings.switchToListView)
            : t(strings.switchToGridView)
        "
        @click="toggleViewMode"
      />
      <v-btn
        icon="mdi-filter-variant"
        variant="outlined"
        :color="hasActiveFilters ? 'primary' : 'grey'"
        :title="t(strings.toggleFilters)"
        class="d-md-none"
        @click="filterSheetOpen = true"
      />
    </div>

    <div v-if="hasActiveFilters" class="parts-filter-chips-wrap mb-4">
      <div
        v-if="activeFilterChips.length > 0"
        class="d-flex flex-wrap ga-2 align-center mb-2"
      >
        <v-chip
          v-for="chip in activeFilterChips"
          :key="chip.id"
          closable
          variant="flat"
          density="comfortable"
          class="parts-filter-chip text-body-2"
          @click:close="clearFilterChipGroup(chip.id)"
        >
          {{ chip.text }}
        </v-chip>
      </div>
      <button
        type="button"
        class="parts-filter-clear-all text-primary text-body-2"
        @click="clearFilters"
      >
        {{ t(strings.clearAllFilters) }}
      </button>
    </div>

    <v-bottom-sheet v-model="filterSheetOpen">
      <v-card class="parts-filter-sheet d-flex flex-column rounded-t-lg">
        <PartsFilterPanel
          :manufacturers="selectedManufacturers"
          :conditions="selectedConditions"
          :part-types="selectedPartTypes"
          :manufacturer-options="manufacturerOptions"
          :condition-options="conditionOptions"
          :part-type-options="partTypeOptions"
          :condition-display-name="conditionDisplayName"
          :initial-expanded="[]"
          :show-header="true"
          :closable="true"
          :show-done="true"
          @update:manufacturers="selectedManufacturers = $event"
          @update:conditions="selectedConditions = $event"
          @update:part-types="selectedPartTypes = $event"
          @done="filterSheetOpen = false"
          @clear="clearFilters"
          @close="filterSheetOpen = false"
        />
      </v-card>
    </v-bottom-sheet>

    <!-- Grid -->
    <template v-if="viewMode === 'grid'">
      <v-row class="parts-grid" density="comfortable">
        <v-col
          v-for="item in paginatedGridParts"
          :key="item.id"
          cols="6"
          sm="6"
          md="4"
          lg="3"
        >
          <v-card
            variant="outlined"
            class="part-grid-card h-100 cursor-pointer d-flex flex-column"
            rounded="lg"
            @click="onView(item)"
          >
            <div class="part-grid-media flex-grow-0">
              <v-img
                v-if="item.thumbnail_url"
                :src="item.thumbnail_url"
                :alt="`Thumbnail for ${item.part_number}`"
                aspect-ratio="1"
                cover
                class="rounded-t-lg"
              />
              <div
                v-else
                class="d-flex align-center justify-center bg-grey-lighten-4 rounded-t-lg"
                style="aspect-ratio: 1"
              >
                <v-icon color="grey-lighten-1" size="40">mdi-image-off</v-icon>
              </div>
            </div>
            <v-card-text class="pt-3 pb-3">
              <div class="text-body-large text-truncate">
                {{ item.part_type || "—" }}
              </div>
              <div class="text-body-2 text-medium-emphasis text-truncate">
                {{ item.part_number }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <div v-if="gridPageCount > 1" class="d-flex justify-center mt-4">
        <v-pagination
          v-model="gridPage"
          :length="gridPageCount"
          :total-visible="7"
          rounded="circle"
        />
      </div>
    </template>

    <!-- List -->
    <v-data-table
      v-else
      v-model:page="listPage"
      :headers="listHeaders"
      :items="filteredParts"
      :items-per-page="ITEMS_PER_PAGE"
      class="elevation-1 parts-list-table"
    >
      <template v-slot:item="{ item }">
        <tr class="parts-list-row" @click="onView(item)">
          <td class="py-3" style="width: 96px">
            <div v-if="item.thumbnail_url" class="d-flex align-center">
              <v-img
                :src="item.thumbnail_url"
                :alt="`Thumbnail for ${item.part_number}`"
                width="80"
                height="80"
                cover
                class="rounded-lg"
              />
            </div>
            <div
              v-else
              class="d-flex align-center justify-center bg-grey-lighten-4 rounded-lg"
              style="width: 80px; height: 80px"
            >
              <v-icon color="grey-lighten-1" size="small">mdi-image-off</v-icon>
            </div>
          </td>
          <td class="py-3">
            <span class="font-weight-medium">{{ item.part_type || "—" }}</span>
          </td>
          <td class="py-3 text-medium-emphasis">
            {{ item.part_number }}
          </td>
        </tr>
      </template>
    </v-data-table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { parts_table_strings as strings } from "./PartsTable.strings.ts";
import { useReverseT } from "../../i18n.ts";
import { getTanstackErrorMessage, TanstackError } from "@saflib/sdk";
import type { Part, PartFile } from "@vendata/power-up-spec";
import { computed, ref, watch } from "vue";
import PartsFilterPanel from "./PartsFilterPanel.vue";

const ITEMS_PER_PAGE = 12;

function scrollToTopOfPage() {
  window.scrollTo({ top: 0, behavior: "smooth" });
}

const { t } = useReverseT();

interface Props {
  parts: Part[];
  partFiles?: PartFile[];
  loading?: boolean;
  error?: TanstackError;
  isAdmin?: boolean;
}

const props = defineProps<Props>();

// Filter reactive variables
const filterText = ref("");
const selectedManufacturers = ref<string[]>([]);
const selectedConditions = ref<string[]>([]);
const selectedPartTypes = ref<string[]>([]);
const filterSheetOpen = ref(false);

const viewMode = ref<"grid" | "list">("grid");
const gridPage = ref(1);
const listPage = ref(1);

// Emits
const emit = defineEmits<{
  view: [part: Part];
}>();

const onView = (part: Part) => {
  emit("view", part);
};

const toggleViewMode = () => {
  viewMode.value = viewMode.value === "grid" ? "list" : "grid";
};

/**
 * Desktop sidebar "Done" handler. Filters apply reactively so there's nothing
 * to commit — we just scroll the parts grid back to the top so the user sees
 * the first matching row after applying selections. The bottom-sheet Done
 * handler still closes the sheet (handled inline on the @done binding).
 */
function onDesktopFilterDone() {
  scrollToTopOfPage();
}

const getErrorMessage = (error: any) => {
  if (!error) return "";
  if (typeof error === "string") return error;
  if (error.message) return error.message;
  return getTanstackErrorMessage(error);
};

/** Known condition enum values → display labels (matches prior static filter list). */
const CONDITION_LABELS: Record<string, string> = {
  new: "New",
  repairable: "Repairable",
  repaired: "Repaired",
  "as removed": "As Removed",
  inspected: "Inspected",
  scrap: "Scrap",
};

function conditionDisplayName(value: string): string {
  const key = value.trim().toLowerCase();
  return CONDITION_LABELS[key] ?? value;
}

interface PartsFilterOption {
  value: string;
  label: string;
}

function uniqueFieldValues(
  parts: Part[],
  pick: (p: Part) => string | null | undefined,
): string[] {
  const set = new Set<string>();
  for (const p of parts) {
    const raw = pick(p);
    if (raw == null) continue;
    const v = String(raw).trim();
    if (!v) continue;
    set.add(v);
  }
  return [...set];
}

/** Checkbox options from distinct values present on the full parts list (no counts). */
const manufacturerOptions = computed((): PartsFilterOption[] => {
  if (!props.parts?.length) return [];
  return uniqueFieldValues(props.parts, (p) => p.manufacturer)
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({ value, label: value }));
});

const conditionOptions = computed((): PartsFilterOption[] => {
  if (!props.parts?.length) return [];
  return uniqueFieldValues(props.parts, (p) => p.condition ?? null)
    .sort((a, b) =>
      conditionDisplayName(a).localeCompare(conditionDisplayName(b)),
    )
    .map((value) => ({
      value,
      label: conditionDisplayName(value),
    }));
});

const partTypeOptions = computed((): PartsFilterOption[] => {
  if (!props.parts?.length) return [];
  return uniqueFieldValues(props.parts, (p) => p.part_type ?? null)
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({ value, label: value }));
});

function applyAllFilters(parts: Part[]): Part[] {
  let filtered = parts;
  const q = filterText.value.trim();
  if (q) {
    const searchText = q.toLowerCase();
    filtered = filtered.filter((part) => {
      const textFields = [
        part.part_number,
        part.part_type,
        part.condition,
        part.upgrades,
        part.quantity?.toString(),
        part.manufacturer,
        part.turbine_model,
        part.frame,
      ].filter(Boolean);

      return textFields.some((field) =>
        field?.toLowerCase().includes(searchText),
      );
    });
  }

  if (selectedManufacturers.value.length > 0) {
    filtered = filtered.filter((part) =>
      selectedManufacturers.value.includes(part.manufacturer),
    );
  }

  if (selectedConditions.value.length > 0) {
    filtered = filtered.filter(
      (part) =>
        part.condition && selectedConditions.value.includes(part.condition),
    );
  }

  if (selectedPartTypes.value.length > 0) {
    filtered = filtered.filter(
      (part) =>
        part.part_type && selectedPartTypes.value.includes(part.part_type),
    );
  }

  return filtered;
}

// Check if any filters are active
const hasActiveFilters = computed(() => {
  return !!(
    filterText.value.trim() ||
    selectedManufacturers.value.length > 0 ||
    selectedConditions.value.length > 0 ||
    selectedPartTypes.value.length > 0
  );
});

type PartsFilterChipId = "search" | "manufacturer" | "condition" | "partType";

/** One chip per active filter group; multi-selects joined with " / ". */
const activeFilterChips = computed(
  (): { id: PartsFilterChipId; text: string }[] => {
    const chips: { id: PartsFilterChipId; text: string }[] = [];
    const q = filterText.value.trim();
    if (q) {
      chips.push({ id: "search", text: q });
    }
    if (selectedManufacturers.value.length > 0) {
      chips.push({
        id: "manufacturer",
        text: [...selectedManufacturers.value]
          .sort((a, b) => a.localeCompare(b))
          .join(" / "),
      });
    }
    if (selectedConditions.value.length > 0) {
      chips.push({
        id: "condition",
        text: [...selectedConditions.value]
          .sort((a, b) =>
            conditionDisplayName(a).localeCompare(conditionDisplayName(b)),
          )
          .map(conditionDisplayName)
          .join(" / "),
      });
    }
    if (selectedPartTypes.value.length > 0) {
      chips.push({
        id: "partType",
        text: [...selectedPartTypes.value]
          .sort((a, b) => a.localeCompare(b))
          .join(" / "),
      });
    }
    return chips;
  },
);

function clearFilterChipGroup(id: PartsFilterChipId) {
  switch (id) {
    case "search":
      filterText.value = "";
      break;
    case "manufacturer":
      selectedManufacturers.value = [];
      break;
    case "condition":
      selectedConditions.value = [];
      break;
    case "partType":
      selectedPartTypes.value = [];
      break;
  }
}

// Clear all filters
const clearFilters = () => {
  filterText.value = "";
  selectedManufacturers.value = [];
  selectedConditions.value = [];
  selectedPartTypes.value = [];
};

const filteredParts = computed(() => {
  if (!props.parts) {
    return [];
  }
  return applyAllFilters(props.parts);
});

watch(
  () =>
    [
      filterText.value,
      selectedManufacturers.value,
      selectedConditions.value,
      selectedPartTypes.value,
    ] as const,
  () => {
    gridPage.value = 1;
    listPage.value = 1;
  },
  { deep: true },
);

watch(gridPage, (_page, prevPage) => {
  if (prevPage === undefined) return;
  scrollToTopOfPage();
});

watch(listPage, (_page, prevPage) => {
  if (prevPage === undefined) return;
  scrollToTopOfPage();
});

const gridPageCount = computed(() =>
  Math.max(1, Math.ceil(filteredParts.value.length / ITEMS_PER_PAGE)),
);

watch(gridPageCount, (count) => {
  if (gridPage.value > count) {
    gridPage.value = count;
  }
});

const paginatedGridParts = computed(() => {
  const page = Math.min(Math.max(1, gridPage.value), gridPageCount.value);
  const start = (page - 1) * ITEMS_PER_PAGE;
  return filteredParts.value.slice(start, start + ITEMS_PER_PAGE);
});

const listHeaders = computed(() => [
  {
    title: t(strings.photoColumn),
    key: "photo",
    sortable: false,
    width: "96px",
  },
  { title: t(strings.partTypeColumn), key: "part_type", sortable: true },
  { title: t(strings.partNumberColumn), key: "part_number", sortable: true },
]);
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.parts-list-row {
  cursor: pointer;
}

.parts-list-table :deep(tbody tr:hover) {
  background-color: rgba(0, 0, 0, 0.04);
}

/*
 * Layout shell.
 *  - Mobile / tablet < md (~960px): single column. The filter sidebar is
 *    hidden via `d-none d-md-flex`; users access the same filter UI via
 *    the filter button + bottom sheet below.
 *  - md+ (tablet landscape + desktop): a 280px filter sidebar + flexible
 *    main column. The sidebar is sticky so the filter accordion stays in
 *    view as the table scrolls.
 */
.parts-table-shell {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 24px;
}

@media (min-width: 960px) {
  .parts-table-shell {
    grid-template-columns: 280px minmax(0, 1fr);
    align-items: start;
  }
}

.parts-table-shell__sidebar {
  position: sticky;
  /* App bar is 80px tall + a comfortable 16px gap */
  top: 96px;
  max-height: calc(100dvh - 112px);
  border: 1px solid
    rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
  background: rgb(var(--v-theme-surface));
  overflow: hidden;
}

.parts-table-shell__main {
  min-width: 0;
}

.parts-filter-sheet {
  max-height: 100dvh;
  overflow: hidden;
}

.parts-filter-chip {
  background-color: #f1f1f1 !important;
  color: rgb(var(--v-theme-on-surface)) !important;
  border-radius: 8px !important;
  max-width: 100%;
}

.parts-filter-chip :deep(.v-chip__content) {
  overflow: hidden;
  text-overflow: ellipsis;
}

.parts-filter-chip :deep(.v-chip__close) {
  opacity: 0.65;
}

.parts-filter-chip :deep(.v-chip__close:hover) {
  opacity: 1;
}

.parts-filter-clear-all {
  display: inline;
  padding: 0;
  margin: 0;
  background: none;
  border: none;
  cursor: pointer;
  font: inherit;
  text-decoration: underline;
  text-underline-offset: 2px;
}
</style>
